import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { DefaultButton } from "@fluentui/react/lib/Button";
import type { EmailProviderSetup } from "@src/types/api";
import {
  readIntegrationDebugFromStorage,
  type IntegrationDebugPayload,
} from "@src/load-agent/utils/integrationDebugStorage";
import { IntegrationDebugAccordion } from "./IntegrationDebugAccordion";

const fabStyle: React.CSSProperties = {
  position: "fixed",
  right: 12,
  bottom: 12,
  width: 28,
  height: 28,
  borderRadius: "50%",
  border: "1px solid #8a8886",
  backgroundColor: "#f3f2f1",
  color: "#323130",
  fontSize: 14,
  fontWeight: 600,
  fontStyle: "italic",
  cursor: "pointer",
  zIndex: 1001,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  lineHeight: 1,
  boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
};
const overlayStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};
const modalStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  padding: "1.25rem",
  backgroundColor: "#fff",
  borderRadius: "4px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
  maxWidth: "min(92vw, 720px)",
  width: "90%",
  maxHeight: "85vh",
  overflow: "auto",
};
const sectionTitleStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#323130",
  margin: "0 0 6px 0",
};
const rowStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "#323130",
  lineHeight: 1.45,
  margin: "4px 0",
};
const mutedStyle: React.CSSProperties = {
  fontSize: "12px",
  color: "#605e5c",
  margin: 0,
};
const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  marginTop: "8px",
};

function kindLabel(kind: string): string {
  return kind === "tms" ? "TMS" : "Rating engine";
}

function renderProviderBlock(
  title: string,
  body: React.ReactNode,
  emptyHint: string
): React.ReactNode {
  return (
    <div>
      <h3 style={sectionTitleStyle}>{title}</h3>
      {body ?? <p style={mutedStyle}>{emptyHint}</p>}
    </div>
  );
}

export default function ProviderSetupInfoBubble({
  providerSetup,
  selectedMode,
}: {
  providerSetup?: EmailProviderSetup | null;
  selectedMode?: "LTL" | "FTL" | null;
}) {
  const [open, setOpen] = useState(false);
  const [storedIntegrationDebug, setStoredIntegrationDebug] =
    useState<IntegrationDebugPayload | null>(null);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) close();
    },
    [close]
  );
  const openModal = useCallback(() => {
    setOpen(true);
  }, []);

  useEffect(() => {
    if (!open) {
      setStoredIntegrationDebug(null);
      return;
    }
    const refresh = () => setStoredIntegrationDebug(readIntegrationDebugFromStorage());

    refresh();
    const timer = window.setInterval(refresh, 1000);

    return () => window.clearInterval(timer);
  }, [open]);
  const payload = providerSetup;
  const missingHint =
    payload == null ? (
      <p style={{ ...rowStyle, color: "#605e5c" }}>
        Provider details are included with loaded email data. If this stays empty, refresh the
        taskpane or ensure the email is ingested.
      </p>
    ) : null;
  const tmsBody =
    payload?.tms != null ? (
      <div>
        <p style={rowStyle}>
          <strong>{payload.tms.name}</strong>
          <span style={mutedStyle}> ({kindLabel(payload.tms.kind)})</span>
        </p>
      </div>
    ) : null;
  const enginesBody =
    payload != null && payload.ratingEngines.length > 0 ? (
      <ul style={{ margin: "4px 0 0 0", paddingLeft: "1.1rem" }}>
        {payload.ratingEngines.map((e) => (
          <li key={e.id} style={rowStyle}>
            <strong>{e.name}</strong>
          </li>
        ))}
      </ul>
    ) : null;
  const showRatingEngines = selectedMode !== "LTL";

  return (
    <>
      {!open ? (
        <button
          type="button"
          style={fabStyle}
          aria-label="Integration setup: TMS and rating engines"
          title="TMS and rating engine setup"
          onClick={openModal}
        >
          i
        </button>
      ) : null}
      {open ? (
        <div style={overlayStyle} onMouseDown={handleOverlayMouseDown} role="presentation">
          <div
            style={modalStyle}
            role="dialog"
            aria-modal="true"
            aria-labelledby="provider-setup-title"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <h2
              id="provider-setup-title"
              style={{ fontSize: "16px", fontWeight: 600, margin: 0, color: "#323130" }}
            >
              Integration setup
            </h2>
            <p style={mutedStyle}>
              TMS and rating engines for your organization (from the email load response — same
              provider registry as quoting and booking).
            </p>
            {missingHint}
            {payload != null ? (
              <>
                {renderProviderBlock("TMS", tmsBody, "No TMS is configured for this organization.")}
                {showRatingEngines
                  ? renderProviderBlock(
                      "Rating engines",
                      enginesBody,
                      "No rating engines are configured for this organization."
                    )
                  : null}
                <IntegrationDebugAccordion
                  stored={storedIntegrationDebug}
                  selectedMode={selectedMode}
                />
              </>
            ) : null}
            {payload == null ? (
              <IntegrationDebugAccordion
                stored={storedIntegrationDebug}
                selectedMode={selectedMode}
              />
            ) : null}
            <div style={footerStyle}>
              <DefaultButton text="Close" onClick={close} />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
